import {
  IntegrationError,
  ProductType,
  PurchasableProduct,
  UserProduct,
  UserSubscription,
} from '@accedo/one-plugin-sdk';
import { useCallback, useEffect, useState } from 'react';

import { Errors } from '#/errors/errors';
import { mapToOneError, OneError } from '#/errors/oneError';
import { useFetchUserProducts } from '#/pages/Settings/hooks/useFetchUserProducts';
import { ignoreError } from '#/service/utils';
import { authActions } from '#/store/auth/auth.store';
import { paywallActions, usePaywallStore } from '#/store/paywall/paywall.store';
import { findPlanByProductIdentifier } from '#/store/paywall/paywall.utils';

import {
  NextSubscriptionDetails,
  PurchaseFlowStore,
} from './PlanPurchaseFlow.types';
import { useSendSubscriptionChangeEvent } from './sendSubscriptionChangeEvent.hook';

const subscriptionMatches = (
  item: UserProduct,
  productIdentifier: string
): item is UserSubscription => {
  return (
    paywallActions.transformProductIdentifierSafely(item.productIdentifier) ===
    paywallActions.transformProductIdentifierSafely(productIdentifier)
  );
};

export const useChangeProductConfirmation = (
  store: PurchaseFlowStore,
  purchasableProducts: PurchasableProduct[]
) => {
  const {
    paywallApi,
    tempSession: { productsResponse },
  } = usePaywallStore();

  const { selectedProduct, direction } = store;

  const [error, setError] = useState<Error | OneError>();
  const [isChangeLoading, setIsChangeLoading] = useState(false);
  const [isChangeComplete, setIsChangeComplete] = useState(false);
  const [nextSubscription, setNextSubscription] =
    useState<NextSubscriptionDetails>();

  const [currentSubscription, setCurrentSubscription] =
    useState<UserSubscription>();

  const packageDefinition = productsResponse?.packageDefinition;

  const { items } = useFetchUserProducts(packageDefinition);
  const { sendSubscriptionChangeEvent } = useSendSubscriptionChangeEvent();

  // Retrieve the current subscription that matches the selected X-Grade user's choice earlier in the paywall flow.
  useEffect(() => {
    if (currentSubscription || error || nextSubscription) {
      return;
    }

    const matchedSubscription = items?.find((item) => {
      const product = item.userProduct;

      if (
        subscriptionMatches(
          product,
          store.direction?.changeFromProductIdentifier ?? ''
        )
      ) {
        setCurrentSubscription(product);

        return true;
      }

      return false;
    });

    if (!matchedSubscription) {
      setError(new OneError(Errors.PURCHASE_PLAN_CHANGE_UNKNOWN));
    }
  }, [nextSubscription, error, currentSubscription, items, store.direction]);

  // Determine the next subscription details based on the users selected product
  useEffect(() => {
    if (!currentSubscription || error || nextSubscription) {
      return;
    }

    const changeToSubscription = purchasableProducts.find(
      (product) => product.id === selectedProduct?.id
    );

    if (!changeToSubscription || !selectedProduct) {
      setError(new OneError(Errors.PURCHASE_PLAN_CHANGE_ITEM_NOT_FOUND));

      return;
    }

    if (!direction || direction.action === 'PURCHASE') {
      setError(new OneError(Errors.PURCHASE_PLAN_CHANGE_INVALID_DIRECTION));

      return;
    }

    if (changeToSubscription.type !== ProductType.Subscription) {
      setError(
        new OneError(Errors.PURCHASE_PLAN_CHANGE_UNSUPPORTED_PRODUCT_TYPE, {
          extras: { productType: changeToSubscription.type },
        })
      );

      return;
    }

    // we narrowed this above to exclude NONE, but TS needs some help to keep this in mind for the async call below
    const narrowDirection = direction.action;

    Promise.resolve(
      paywallApi.getChangeUserProductInfo?.(
        currentSubscription.productIdentifier,
        selectedProduct.id,
        direction.action
      )
    )
      .then((res) => {
        const info = res ?? changeToSubscription;
        setNextSubscription({
          id: selectedProduct.id,
          title: info.title,
          price: info.price,
          currency: info.currency,
          duration: changeToSubscription.duration,
          direction: narrowDirection,
        });
      })
      .catch((e: Error | IntegrationError) => {
        setError(mapToOneError(e));
      });
  }, [
    nextSubscription,
    error,
    purchasableProducts,
    selectedProduct,
    direction,
    currentSubscription,
    paywallApi,
  ]);

  const handleChangeProduct = useCallback(async () => {
    setIsChangeLoading(true);

    if (nextSubscription && currentSubscription) {
      try {
        const res = await paywallApi.changeUserProduct!(
          currentSubscription,
          nextSubscription.id,
          nextSubscription.direction
        );

        setNextSubscription({
          ...nextSubscription,
          changeDate: res.changeDate,
          isImmediateSwitch: res.isImmediateSwitch,
        });

        await authActions.refreshProfileAfterEntitlementPurchase();

        if (!res.isImmediateSwitch) {
          paywallActions.refreshProducts();
        }

        const subscriptionGroups = packageDefinition?.subscriptionGroups ?? [];

        const planFrom = findPlanByProductIdentifier(
          subscriptionGroups,
          currentSubscription.productIdentifier,
          paywallActions.transformProductIdentifierSafely
        );

        const planTo = findPlanByProductIdentifier(
          subscriptionGroups,
          nextSubscription.id,
          paywallActions.transformProductIdentifierSafely
        );

        sendSubscriptionChangeEvent(
          res,
          nextSubscription,
          planFrom,
          planTo
        ).catch(ignoreError);
      } catch (error) {
        setError(mapToOneError(error));
      }
    } else {
      setError(new OneError(Errors.PURCHASE_PLAN_CHANGE_UNKNOWN));
    }

    setIsChangeLoading(false);
    setIsChangeComplete(true);
  }, [
    paywallApi,
    currentSubscription,
    nextSubscription,
    packageDefinition,
    sendSubscriptionChangeEvent,
  ]);

  return {
    isChangeLoading,
    isChangeComplete,
    handleChangeProduct,
    currentSubscription,
    nextSubscription,
    purchasableProducts,
    error,
  };
};
